<template>
  <div class="media-page page-vod h-full w-full p-4">
    <section class="media-hero mb-5">
      <div>
        <h2 class="text-xl font-bold mb-2">Videos a Demanda</h2>
        <p class="hero-subtitle">Sube videos MP4 para reproducirse en el canal cuando el usuario los solicite.</p>
      </div>
    </section>

    <section class="media-toolbar mb-4">
      <div class="toolbar-field">
        <label class="field-label block mb-1">Canal</label>
        <Dropdown
          v-model="selectedRadio"
          :options="radios"
          optionLabel="name"
          optionValue="id"
          placeholder="Selecciona un canal"
          class="channel-select w-full"
          filter
          filterPlaceholder="Buscar canal..."
          :showClear="false"
          @change="loadItems"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value && findRadioById(slotProps.value)" class="channel-option-content">
              <div class="channel-logo-shell">
                <img
                  v-if="getRadioLogo(findRadioById(slotProps.value))"
                  :src="getRadioLogo(findRadioById(slotProps.value))"
                  :alt="findRadioById(slotProps.value).name"
                  class="channel-logo"
                  @error="onRadioLogoError(findRadioById(slotProps.value))"
                />
                <span v-else class="channel-logo-fallback">{{ getRadioInitials(findRadioById(slotProps.value).name) }}</span>
              </div>
              <span class="channel-option-label">{{ findRadioById(slotProps.value).name }}</span>
            </div>
            <span v-else class="channel-placeholder">Selecciona un canal</span>
          </template>

          <template #option="slotProps">
            <div class="channel-option-content">
              <div class="channel-logo-shell">
                <img
                  v-if="getRadioLogo(slotProps.option)"
                  :src="getRadioLogo(slotProps.option)"
                  :alt="slotProps.option.name"
                  class="channel-logo"
                  @error="onRadioLogoError(slotProps.option)"
                />
                <span v-else class="channel-logo-fallback">{{ getRadioInitials(slotProps.option.name) }}</span>
              </div>
              <span class="channel-option-label">{{ slotProps.option.name }}</span>
            </div>
          </template>
        </Dropdown>
      </div>

      <div class="toolbar-actions">
        <Button
          label="Subir video"
          icon="pi pi-upload"
          class="icon-button rounded upload-action-btn"
          @click="openUploadDialog"
          :disabled="!selectedRadio"
        />
      </div>
    </section>

    <section v-if="selectedRadio" class="media-table-wrap">
      <DataTable
        :value="items"
        :loading="loading"
        responsiveLayout="scroll"
        class="p-datatable-sm media-table"
      >
        <Column header="Preview" style="width: 110px">
          <template #body="slotProps">
            <video
              :src="slotProps.data.file"
              class="w-20 h-12 object-cover rounded cursor-pointer"
              muted
              @click="previewItem(slotProps.data)"
            />
          </template>
        </Column>

        <Column field="name" header="Titulo" />

        <Column header="URL" style="min-width: 280px">
          <template #body="slotProps">
            <a :href="slotProps.data.file" target="_blank" rel="noopener" class="url-link break-all">
              {{ slotProps.data.file }}
            </a>
          </template>
        </Column>

        <Column header="Orden" style="width: 110px">
          <template #body="slotProps">
            <div class="flex gap-1">
              <Button
                icon="pi pi-arrow-up"
                severity="secondary"
                text
                size="small"
                @click="moveUp(slotProps.index)"
                :disabled="slotProps.index === 0"
              />
              <Button
                icon="pi pi-arrow-down"
                severity="secondary"
                text
                size="small"
                @click="moveDown(slotProps.index)"
                :disabled="slotProps.index === items.length - 1"
              />
            </div>
          </template>
        </Column>

        <Column header="Activo" style="width: 70px">
          <template #body="slotProps">
            <Checkbox v-model="slotProps.data.active" :binary="true" @change="toggleActive(slotProps.data)" />
          </template>
        </Column>

        <Column header="Acciones" style="width: 110px">
          <template #body="slotProps">
            <div class="flex gap-1">
              <Button icon="pi pi-pencil" severity="info" text size="small" @click="openEditDialog(slotProps.data)" />
              <Button icon="pi pi-trash" severity="danger" text size="small" @click="confirmDelete(slotProps.data)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </section>

    <section v-else class="media-empty-state">
      <i class="pi pi-video media-empty-icon"></i>
      <p class="media-empty-title">Selecciona un canal</p>
      <p class="media-empty-hint">Administra la biblioteca VOD, orden de reproduccion y activacion por canal.</p>
    </section>

    <Dialog
      v-model:visible="uploadDialogVisible"
      modal
      header="Subir video a demanda"
      :style="{ width: '55vw' }"
      :breakpoints="{ '1199px': '80vw', '575px': '95vw' }"
    >
      <div class="grid grid-cols-1 gap-3">
        <div>
          <label class="field-label block mb-1">Titulo</label>
          <InputText v-model="form.name" class="w-full" maxlength="191" placeholder="Nombre del video" />
        </div>

        <div>
          <label class="field-label block mb-1">Archivo MP4 (max 250 MB)</label>
          <input
            type="file"
            @change="onFileSelect($event.target.files[0])"
            accept="video/mp4"
            class="w-full"
          />
        </div>
      </div>

      <div v-if="previewUrl" class="mt-3">
        <video :src="previewUrl" controls class="max-h-48 rounded" />
      </div>

      <div class="w-full bg-gray-200 rounded-full h-2.5 my-3" v-if="uploading">
        <div class="bg-violet-600 h-2.5 rounded-full" :style="{ width: uploadProgress + '%' }"></div>
      </div>

      <template #footer>
        <Button
          label="Subir"
          icon="pi pi-upload"
          severity="success"
          outlined
          class="icon-button rounded"
          @click="uploadFile"
          :disabled="!selectedFile || uploading || !form.name"
        />
        <Button
          label="Cerrar"
          icon="pi pi-times"
          severity="info"
          outlined
          class="icon-button rounded"
          @click="uploadDialogVisible = false"
          :disabled="uploading"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="editDialogVisible"
      modal
      header="Editar video a demanda"
      :style="{ width: '45vw' }"
      :breakpoints="{ '1199px': '70vw', '575px': '95vw' }"
    >
      <div v-if="editForm" class="grid grid-cols-1 gap-3">
        <div>
          <label class="field-label block mb-1">Titulo</label>
          <InputText v-model="editForm.name" class="w-full" maxlength="191" />
        </div>
      </div>

      <template #footer>
        <Button
          label="Guardar"
          icon="pi pi-check"
          severity="success"
          outlined
          class="icon-button rounded"
          @click="saveEdit"
          :disabled="savingEdit || !editForm || !editForm.name"
        />
        <Button
          label="Cancelar"
          icon="pi pi-times"
          severity="info"
          outlined
          class="icon-button rounded"
          @click="editDialogVisible = false"
          :disabled="savingEdit"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="previewDialogVisible"
      modal
      header="Vista previa"
      :style="{ width: '60vw' }"
      :breakpoints="{ '575px': '90vw' }"
    >
      <div class="flex justify-center" v-if="previewData">
        <video :src="previewData.file" controls autoplay class="max-h-96 rounded" />
      </div>
    </Dialog>

    <ConfirmDialog />
    <Toast position="top-right" group="topr" />
  </div>
</template>

<script>
import axiosInstance from '../axiosInstance';

const MAX_FILE_SIZE = 250 * 1024 * 1024;
const CDN_PREFIX = 'https://media.vybroo.com/videos_on_demand/';

export default {
  data() {
    return {
      radios: [],
      selectedRadio: null,
      items: [],
      loading: false,

      uploadDialogVisible: false,
      form: this.emptyForm(),
      selectedFile: null,
      previewUrl: null,
      uploading: false,
      uploadProgress: 0,

      editDialogVisible: false,
      editForm: null,
      savingEdit: false,

      previewDialogVisible: false,
      previewData: null,
    };
  },
  mounted() {
    this.loadRadios();
  },
  methods: {
    findRadioById(radioId) {
      return this.radios.find((radio) => String(radio.id) === String(radioId)) || null;
    },

    normalizeLogoUrl(value) {
      if (!value || typeof value !== 'string') return null;
      if (value.startsWith('http://') || value.startsWith('https://')) return value;
      if (value.startsWith('//')) return 'https:' + value;
      if (value.startsWith('/')) return value;
      return null;
    },

    getRadioLogo(radio) {
      if (!radio || radio._logoBroken) return null;

      const logoKeys = ['logo', 'logo_url', 'logoUrl', 'image', 'image_url', 'thumbnail', 'thumb', 'icon', 'avatar'];
      for (const key of logoKeys) {
        const normalized = this.normalizeLogoUrl(radio[key]);
        if (normalized) return normalized;
      }

      return null;
    },

    getRadioInitials(name) {
      if (!name || typeof name !== 'string') return 'R';
      return name.trim().charAt(0).toUpperCase() || 'R';
    },

    onRadioLogoError(radio) {
      if (radio) {
        radio._logoBroken = true;
      }
    },

    emptyForm() {
      return {
        name: '',
      };
    },

    async loadRadios() {
      try {
        const res = await axiosInstance.post('/api/radios', {
          token: localStorage.getItem('token')
        });
        const rows = res.data.items || [];
        this.radios = rows;
      } catch (e) {
        console.error('Error cargando radios:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los canales', life: 5000, group: 'topr' });
      }
    },

    async loadItems() {
      if (!this.selectedRadio) return;
      this.loading = true;
      try {
        const res = await axiosInstance.post('/api/on_demand_videos_list', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio
        });
        this.items = res.data.items || [];
      } catch (e) {
        console.error('Error cargando videos on-demand:', e);
        this.items = [];
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la lista para este canal', life: 5000, group: 'topr' });
      } finally {
        this.loading = false;
      }
    },

    openUploadDialog() {
      this.form = this.emptyForm();
      this.selectedFile = null;
      this.previewUrl = null;
      this.uploading = false;
      this.uploadProgress = 0;
      this.uploadDialogVisible = true;
    },

    onFileSelect(file) {
      if (!file) return;
      if (file.type !== 'video/mp4') {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Solo se permite archivo MP4', life: 5000, group: 'topr' });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'El archivo excede 250 MB', life: 5000, group: 'topr' });
        return;
      }
      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    async uploadFile() {
      if (!this.selectedRadio || !this.selectedFile || !this.form.name) return;

      this.uploading = true;
      this.uploadProgress = 0;

      const timestamp = Date.now();
      const fileName = 'r' + this.selectedRadio + '_' + timestamp + '.mp4';
      const storagePath = 'videos_on_demand/' + fileName;
      const finalUrl = CDN_PREFIX + fileName;

      try {
        const presignedRes = await axiosInstance.post('/transmito/get_presigned_url', {
          fileName: storagePath
        });

        if (!presignedRes.data) {
          throw new Error('No se obtuvo URL firmada');
        }

        const uploadUrl = presignedRes.data.replace(
          'https://vybroo-media.36849d2e964ee51fea8d00d8d1f680eb.r2.cloudflarestorage.com/',
          'vybroo-media/'
        );

        await axiosInstance({
          url: uploadUrl,
          method: 'PUT',
          data: this.selectedFile,
          headers: { 'Content-Type': 'video/mp4' },
          onUploadProgress: (e) => {
            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
          }
        });

        await axiosInstance.post('/api/on_demand_videos_save', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio,
          name: this.form.name,
          file: finalUrl
        });

        this.$toast.add({ severity: 'success', summary: 'Listo', detail: 'Video a demanda guardado', life: 4000, group: 'topr' });
        this.uploadDialogVisible = false;
        await this.loadItems();
      } catch (e) {
        console.error('Error subiendo video on-demand:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo subir el video', life: 5000, group: 'topr' });
      } finally {
        this.uploading = false;
      }
    },

    openEditDialog(item) {
      this.editForm = {
        id: item.id,
        name: item.name || '',
      };
      this.editDialogVisible = true;
    },

    async saveEdit() {
      if (!this.editForm || !this.editForm.name) return;
      this.savingEdit = true;
      try {
        await axiosInstance.post('/api/on_demand_videos_update', {
          token: localStorage.getItem('token'),
          id: this.editForm.id,
          name: this.editForm.name
        });
        this.$toast.add({ severity: 'success', summary: 'Guardado', detail: 'Video actualizado', life: 3000, group: 'topr' });
        this.editDialogVisible = false;
        await this.loadItems();
      } catch (e) {
        console.error('Error actualizando video:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar', life: 5000, group: 'topr' });
      } finally {
        this.savingEdit = false;
      }
    },

    async moveUp(index) {
      if (index === 0) return;
      const temp = this.items[index];
      this.items[index] = this.items[index - 1];
      this.items[index - 1] = temp;
      await this.saveOrder();
    },

    async moveDown(index) {
      if (index >= this.items.length - 1) return;
      const temp = this.items[index];
      this.items[index] = this.items[index + 1];
      this.items[index + 1] = temp;
      await this.saveOrder();
    },

    async saveOrder() {
      const reordered = this.items.map((item, i) => ({ id: item.id, sort_order: i + 1 }));
      try {
        await axiosInstance.post('/api/on_demand_videos_reorder', {
          token: localStorage.getItem('token'),
          items: reordered
        });
      } catch (e) {
        console.error('Error reordenando videos:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el orden', life: 5000, group: 'topr' });
      }
    },

    async toggleActive(item) {
      try {
        await axiosInstance.post('/api/on_demand_videos_toggle', {
          token: localStorage.getItem('token'),
          id: item.id
        });
      } catch (e) {
        console.error('Error cambiando estado:', e);
        item.active = !item.active;
      }
    },

    confirmDelete(item) {
      this.$confirm.require({
        message: 'Eliminar este video a demanda?',
        header: 'Confirmar',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.deleteItem(item)
      });
    },

    async deleteItem(item) {
      try {
        await axiosInstance.post('/api/on_demand_videos_delete', {
          token: localStorage.getItem('token'),
          id: item.id
        });
        this.$toast.add({ severity: 'success', summary: 'Eliminado', detail: 'Video eliminado', life: 3000, group: 'topr' });
        await this.loadItems();
      } catch (e) {
        console.error('Error eliminando video:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 5000, group: 'topr' });
      }
    },

    previewItem(item) {
      this.previewData = item;
      this.previewDialogVisible = true;
    }
  }
};
</script>

<style scoped>
@import "@/assets/media-module-base.css";

.page-vod {
  --media-hero-bg: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  --media-hero-border: rgba(5, 150, 105, 0.2);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(6, 78, 59, 0.45) 0%, rgba(15, 23, 42, 0.96) 100%);
  --media-hero-border-dark: rgba(16, 185, 129, 0.35);

  --media-control-border: rgba(5, 150, 105, 0.3);
  --media-control-border-hover: rgba(5, 150, 105, 0.62);
  --media-control-focus: rgba(52, 211, 153, 0.2);
  --media-control-border-dark: rgba(16, 185, 129, 0.4);

  --media-cta-bg: #059669;
  --media-cta-bg-hover: #047857;

  --media-table-border: rgba(167, 243, 208, 0.8);
  --media-empty-border: rgba(16, 185, 129, 0.45);

  --media-table-head-bg: #ecfdf5;
  --media-table-head-text: #14532d;
  --media-table-head-border: rgba(110, 231, 183, 0.65);
  --media-table-cell-border: rgba(167, 243, 208, 0.5);

  --media-table-head-bg-dark: rgba(6, 78, 59, 0.35);
  --media-table-head-text-dark: #d1fae5;
  --media-table-head-border-dark: rgba(16, 185, 129, 0.45);
}

.url-link {
  color: #047857;
}

.dark .url-link {
  color: #6ee7b7;
}
</style>
